import { currentFactValue, splitFactKey, type EntityState } from './entities-fold.js';
import { viewerKey, type ViewerId } from './viewer.js';

/**
 * Per-Viewer reveal rollup for ONE entity (the Reveals tab, Phase 2 /
 * `30-design-system.md §7.2`/`§7.7`). Answers the GM's two questions about
 * an entity at a glance:
 *   - "what can each Viewer see here?" → `byViewer`
 *   - "what is still secret?" → `hiddenPredicates`
 *
 * Predicate ids are returned raw (not display labels) — i18n labelling is a
 * client concern. Repeatable-instance fact keys (`predicate#instance`)
 * collapse onto their bare predicate, deduped, since a reveal targets the
 * predicate. Facts with no current value (never written / soft-deleted) are
 * excluded from both buckets. Pure + deterministic (viewers sorted by key,
 * predicates sorted within each).
 */
export interface ViewerRevealGroup {
  viewer: ViewerId;
  /** Bare predicate ids this Viewer can see on the entity, sorted + deduped. */
  predicates: string[];
}

export interface EntityRevealRollup {
  byViewer: ViewerRevealGroup[];
  /** Predicates with a value but revealed to nobody (GM-only). Sorted + deduped. */
  hiddenPredicates: string[];
}

export function entityRevealRollup(entity: EntityState): EntityRevealRollup {
  // viewerKey → { viewer, predicate set }
  const byViewer = new Map<string, { viewer: ViewerId; predicates: Set<string> }>();
  const hidden = new Set<string>();

  for (const [factKey, fact] of Object.entries(entity.facts)) {
    if (currentFactValue(fact) === undefined) continue;
    const predicate = splitFactKey(factKey).predicate;
    if (fact.reveals.length === 0) {
      hidden.add(predicate);
      continue;
    }
    for (const viewer of fact.reveals) {
      const key = viewerKey(viewer);
      let group = byViewer.get(key);
      if (!group) {
        group = { viewer, predicates: new Set() };
        byViewer.set(key, group);
      }
      group.predicates.add(predicate);
    }
  }

  const sortedViewers = [...byViewer.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([, group]) => ({ viewer: group.viewer, predicates: [...group.predicates].sort() }));

  return { byViewer: sortedViewers, hiddenPredicates: [...hidden].sort() };
}
